import { SvgAdd } from '@icons/designSystemIcons/SvgAdd';
import { SvgGoogle } from '@icons/designSystemIcons/SvgGoogle';
import { SvgInjection } from '@icons/designSystemIcons/SvgInjection';
import { SvgMedal } from '@icons/designSystemIcons/SvgMedal';
import { SvgUsers } from '@icons/designSystemIcons/SvgUsers';
import { SvgVerify } from '@icons/designSystemIcons/SvgVerify';
import { Professional } from '@interface/clinic';
import { Text } from 'designSystem/Texts/Texts';

// Ficha del médico que devuelve la API de clínicas (GET /Clinics): cifras,
// formación y experiencia. La usan la página /equipo-medico/[slug], el panel
// de cirujanos de la web y el panel de equipo médico del dashboard.

export type ProfessionalProfileData = Pick<
  Professional,
  | 'medicalCollege'
  | 'medicalDegree'
  | 'masters'
  | 'training'
  | 'experience'
  | 'patientsTreated'
  | 'treatmentsPerformed'
  | 'satisfactionRate'
  | 'rating'
>;

export function hasTraining(professional: ProfessionalProfileData) {
  return Boolean(
    professional.medicalDegree ||
      professional.masters?.length ||
      professional.training?.length
  );
}

export function hasExperience(professional: ProfessionalProfileData) {
  return Boolean(professional.experience?.length);
}

interface Stat {
  Icon: typeof SvgUsers;
  value: string;
  text: string;
  google?: boolean;
}

function getStats(professional: ProfessionalProfileData): Stat[] {
  const stats: Stat[] = [];
  if (professional.patientsTreated) {
    stats.push({
      Icon: SvgUsers,
      value: `+${professional.patientsTreated}`,
      text: 'pacientes atendidos',
    });
  }
  if (professional.treatmentsPerformed) {
    stats.push({
      Icon: SvgInjection,
      value: `+${professional.treatmentsPerformed}`,
      text: 'tratamientos aplicados',
    });
  }
  if (professional.satisfactionRate) {
    stats.push({
      Icon: SvgMedal,
      value: `${professional.satisfactionRate}%`,
      text: 'de clientes satisfechos',
    });
  }
  if (professional.rating) {
    stats.push({
      Icon: SvgVerify,
      value: professional.rating.toFixed(1).replace('.', ','),
      text: 'valoración',
      google: true,
    });
  }
  return stats;
}

export function ProfessionalStats({
  professional,
  className = '',
}: {
  professional: ProfessionalProfileData;
  className?: string;
}) {
  const stats = getStats(professional);
  if (stats.length === 0) return null;

  return (
    <ul className={`flex gap-3 w-full text-hg-black500 ${className}`}>
      {stats.map(({ Icon, value, text, google }) => (
        <li
          key={text}
          className="flex flex-col gap-2 justify-start items-center rounded-2xl bg-derma-secondary500 p-3 md:p-4 flex-1"
        >
          <Icon className="text-hg-secondary h-8 w-8 md:h-10 md:w-10" />
          <Text className="text-center text-hg-secondary text-base md:text-xl font-semibold font-gtUltra">
            {value}
          </Text>
          <Text className="text-center text-hg-black500 text-xs">{text}</Text>
          {google && <SvgGoogle className="text-hg-secondary -mt-5" />}
        </li>
      ))}
    </ul>
  );
}

function ItemList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {items.map(item => (
        <li key={item} className="flex justify-start text-left">
          <SvgAdd className="shrink-0 mr-2 h-5 w-5 text-hg-black400" />
          <div>{item}</div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Titulación, másteres y formación complementaria. `HeadingTag` permite que
 * en la ficha del médico sean h3 (bajo el h2 "Formación") y en los paneles
 * simples párrafos.
 */
export function ProfessionalTraining({
  professional,
  HeadingTag = 'p',
}: {
  professional: ProfessionalProfileData;
  HeadingTag?: 'p' | 'h3';
}) {
  if (!hasTraining(professional)) return null;

  const sections = [
    {
      title: 'Titulación en Medicina',
      items: professional.medicalDegree ? [professional.medicalDegree] : [],
    },
    { title: 'Máster / Posgrado', items: professional.masters || [] },
    {
      title: 'Formación complementaria y cursos',
      items: professional.training || [],
    },
  ].filter(section => section.items.length > 0);

  return (
    <ul className="flex flex-col gap-6 text-left text-hg-black500">
      {sections.map(section => (
        <li key={section.title}>
          <HeadingTag className="font-semibold text-hg-black mb-2">
            {section.title}
          </HeadingTag>
          <ItemList items={section.items} />
        </li>
      ))}
    </ul>
  );
}

export function ProfessionalExperience({
  professional,
}: {
  professional: ProfessionalProfileData;
}) {
  if (!hasExperience(professional)) return null;
  return (
    <div className="text-hg-black500">
      <ItemList items={professional.experience || []} />
    </div>
  );
}
